// C:\\Users\\abdul\\Downloads\\Onebox\\onebox\\src\\components\\Hero.jsx
import React, { useState, useEffect } from 'react';
import { FaFacebookF, FaTwitter, FaPinterestP } from 'react-icons/fa';
import { supabase } from '../supabaseClient'; // ⚠️ IMPORTANT: Make sure this import exists
import './Hero.css';

const Hero = ({ tenant = {} }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch hero data from Supabase
  useEffect(() => {
    if (!tenant?.id) {
      setLoading(false);
      return;
    }

    fetchHeroData();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('hero-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'website_sections',
          filter: `tenant_id=eq.${tenant.id}`,
        },
        (payload) => {
          console.log('🔄 Hero section updated:', payload);
          if (payload.new?.section_name === 'hero') {
            setHeroData(payload.new.content);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tenant?.id]);

  const fetchHeroData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('website_sections')
        .select('content, is_visible')
        .eq('tenant_id', tenant.id)
        .eq('section_name', 'hero')
        .maybeSingle();

      if (error) {
        console.error('Error fetching hero data:', error);
        setHeroData(getDefaultData());
      } else if (data && data.is_visible) {
        setHeroData(data.content);
        console.log('✅ Hero data loaded from database');
      } else {
        setHeroData(getDefaultData());
      }
    } catch (error) {
      console.error('Error in fetchHeroData:', error);
      setHeroData(getDefaultData());
    } finally {
      setLoading(false);
    }
  };

  // Default fallback data
  const getDefaultData = () => ({
    title: 'The Art of speed food Quality',
    subtitle: 'Its Quick & Amusing!',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Varius sed pharetra dictum neque massa congue',
    cta_text: 'See Menu',
    cta_link: '#menu',
    gallery_images: [
      'https://images.unsplash.com/photo-1623428187969-5da2dcea5ebf?w=800&q=80',
      'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&q=80',
      'https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?w=800&q=80',
      'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&q=80',
    ],
    social_links: {
      facebook: '',
      twitter: '',
      pinterest: '',
    },
    is_active: true,
  });

  // Get data with fallbacks
  const data = heroData || getDefaultData();
  const title = data?.title || 'The Art of speed food Quality';
  const subtitle = data?.subtitle || 'Its Quick & Amusing!';
  const description = data?.description || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.';
  const ctaText = data?.cta_text || 'See Menu';
  const ctaLink = data?.cta_link || '#menu';
  const galleryImages = data?.gallery_images || [];
  const socialLinks = data?.social_links || {};

  // Default images if not provided
  const defaultImages = [
    {
      id: 1,
      url: 'https://images.unsplash.com/photo-1623428187969-5da2dcea5ebf?w=800&q=80',
      alt: 'Noodle Bowl'
    },
    {
      id: 2,
      url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=800&q=80',
      alt: 'Pizza'
    },
    {
      id: 3,
      url: 'https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?w=800&q=80',
      alt: 'Salad'
    },
    {
      id: 4,
      url: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800&q=80',
      alt: 'Salad Bowl'
    }
  ];

  // Use gallery images from database or defaults
  const foodImages = galleryImages.length > 0 && galleryImages[0] !== ''
    ? galleryImages
        .filter(url => url && url.trim() !== '') // Filter out empty strings
        .map((url, index) => ({
          id: index + 1,
          url: url,
          alt: `${tenant?.business_name || 'Food'} Image ${index + 1}`
        }))
    : defaultImages;

  // Get primary color from tenant
  const primaryColor = tenant?.primary_color || '#FF9F0D';

  // Auto-rotate carousel every 6 seconds
  useEffect(() => {
    if (foodImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % foodImages.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [foodImages.length]);

  const handleCarouselClick = (index) => {
    setCurrentSlide(index);
  };

  // Exact positions with proper size sequence
  const smallImagePositions = [
    { top: '10%', left: '22%', size: 110, zIndex: 6 },
    { top: '40%', left: '6%', size: 140, zIndex: 5 },
    { top: '85%', left: '25%', size: 100, zIndex: 4 },
  ];

  const getImagePosition = (imageIndex) => {
    if (imageIndex === currentSlide) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        isMain: true,
        size: 420
      };
    } else {
      let queuePosition;
      if (imageIndex > currentSlide) {
        queuePosition = imageIndex - currentSlide - 1;
      } else {
        queuePosition = (foodImages.length - currentSlide) + imageIndex - 1;
      }
      
      queuePosition = queuePosition % smallImagePositions.length;
      const position = smallImagePositions[queuePosition];
      
      return {
        top: position.top,
        left: position.left,
        transform: 'translate(-50%, -50%)',
        zIndex: position.zIndex,
        isMain: false,
        size: position.size
      };
    }
  };

  // Parse title for highlighting (supports "text<|>highlighted text")
  const renderTitle = () => {
    if (title.includes('<|>')) {
      const parts = title.split('<|>');
      return (
        <>
          <span className="title-highlight">{parts[0]}</span>
          <br />
          {parts[1]}
        </>
      );
    }
    return title;
  };

  // Show loading state (optional)
  if (loading) {
    return (
      <section id="home" className="hero-section">
        <div className="hero-container">
          <div className="hero-loading">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="home" className="hero-section">
      <div className="hero-container">
        {/* Left Side - Content */}
        <div className="hero-content">
          {/* Social Media Icons - Vertical - DYNAMIC */}
          <div className="hero-social">
            <div className="social-line"></div>
            {socialLinks?.facebook && (
              <a href={socialLinks.facebook} className="social-icon" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                <FaFacebookF />
              </a>
            )}
            {socialLinks?.twitter && (
              <a href={socialLinks.twitter} className="social-icon" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                <FaTwitter />
              </a>
            )}
            {socialLinks?.pinterest && (
              <a href={socialLinks.pinterest} className="social-icon" aria-label="Pinterest" target="_blank" rel="noopener noreferrer">
                <FaPinterestP />
              </a>
            )}
            {/* Show default icons if no social links */}
            {!socialLinks?.facebook && !socialLinks?.twitter && !socialLinks?.pinterest && (
              <>
                <a href="#facebook" className="social-icon" aria-label="Facebook">
                  <FaFacebookF />
                </a>
                <a href="#twitter" className="social-icon" aria-label="Twitter">
                  <FaTwitter />
                </a>
                <a href="#pinterest" className="social-icon" aria-label="Pinterest">
                  <FaPinterestP />
                </a>
              </>
            )}
            <div className="social-line"></div>
          </div>

          {/* Main Content - DYNAMIC */}
          <div className="hero-text">
            <span 
              className="hero-subtitle"
              style={{ color: primaryColor }}
            >
              {subtitle}
            </span>
            <h1 className="hero-title">
              {renderTitle()}
            </h1>
            <p className="hero-description">
              {description}
            </p>
            {ctaText && (
              <button 
                className="hero-button"
                style={{ backgroundColor: primaryColor }}
                onClick={() => {
                  if (ctaLink.startsWith('#')) {
                    const element = document.querySelector(ctaLink);
                    element?.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    window.location.href = ctaLink;
                  }
                }}
              >
                {ctaText}
              </button>
            )}
          </div>

          {/* Carousel Indicators - Vertical */}
          {foodImages.length > 1 && (
            <div className="hero-carousel">
              {foodImages.map((item, index) => (
                <div
                  key={item.id}
                  className={`carousel-indicator ${currentSlide === index ? 'active' : ''}`}
                  style={currentSlide === index ? { backgroundColor: primaryColor } : {}}
                  onClick={() => handleCarouselClick(index)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right Side - Food Image Carousel */}
        <div className="hero-image">
          <div className="hero-image-wrapper">
            {/* Circular Path with Dynamic Color */}
            <svg className="curved-path" viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M 132 60 A 240 240 0 0 0 36 240 A 240 240 0 0 0 150 510 A 240 240 0 0 0 270 545"
                fill="none"
                stroke={`${primaryColor}80`}
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="132" cy="60" r="4" fill={primaryColor} opacity="0.7" />
              <circle cx="36" cy="240" r="4" fill={primaryColor} opacity="0.7" />
              <circle cx="150" cy="510" r="4" fill={primaryColor} opacity="0.7" />
              <circle cx="270" cy="545" r="6" fill={primaryColor} opacity="0.9" />
            </svg>

            {/* All Images - Smoothly Transitioning */}
            {foodImages.map((image, index) => {
              const position = getImagePosition(index);
              
              return (
                <div
                  key={image.id}
                  className={`food-image ${position.isMain ? 'main-image' : 'small-image'}`}
                  style={{
                    top: position.top,
                    left: position.left,
                    transform: position.transform,
                    zIndex: position.zIndex,
                    width: `${position.size}px`,
                    height: `${position.size}px`,
                  }}
                  onClick={() => !position.isMain && handleCarouselClick(index)}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="food-img"
                    loading={position.isMain ? "eager" : "lazy"}
                  />
                </div>
              );
            })}

            {/* Static Green Leaf SVG */}
            <div className="decorative-leaf">
              <svg viewBox="0 0 300 300" className="leaf-svg" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M 150 50 Q 80 80, 60 150 Q 80 220, 150 250 Q 200 230, 220 180 Q 210 130, 180 100 Q 165 85, 150 50 Z"
                  fill="#52c41a"
                  opacity="0.7"
                />
                <path
                  d="M 150 50 L 140 120 M 150 50 L 160 100 M 150 50 L 130 180"
                  stroke="#3d9614"
                  strokeWidth="2"
                  opacity="0.5"
                  fill="none"
                />
                <ellipse
                  cx="150"
                  cy="150"
                  rx="70"
                  ry="90"
                  fill="none"
                  stroke="#3d9614"
                  strokeWidth="1"
                  opacity="0.3"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;